import React, { useContext, useEffect, useState } from "react"
import { useGetInvoices } from "../../hooks/usePaddleHooks.js"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import { useTranslation } from "react-i18next"

export const InvoiceHistory = () => {
	const { state } = useContext(GlobalStateContext)
	const getInvoices = useGetInvoices()
	const { t } = useTranslation()

	const [initialFetch, setInitialFetch] = useState(false)
	const [invoices, setInvoices] = useState([])
	const [invoicesLoading, setInvoicesLoading] = useState(true)
	const [page, setPage] = useState(1)
	const [cursorUrls, setCursorUrls] = useState([null])

	const changePage = async (queryPage) => {
		if (invoicesLoading) return
		if (queryPage < 1) return
		if (queryPage > 1 && !cursorUrls[queryPage - 1]) return

		setPage(queryPage)
		setInvoicesLoading(true)
		const { invoices, nextCursorUrl } = await getInvoices(
			state.paddleCustomerId,
			cursorUrls[queryPage - 1],
		)
		setInvoices(invoices)

		if (queryPage > page && nextCursorUrl) {
			setCursorUrls([...cursorUrls, nextCursorUrl])
		}
		setInvoicesLoading(false)
	}

	useEffect(async () => {
		if (state.paddleCustomerId && !initialFetch) {
			setInvoicesLoading(true)
			setInitialFetch(true)
			const { invoices, nextCursorUrl } = await getInvoices(
				state.paddleCustomerId,
				cursorUrls[page - 1],
			)
			setInvoices(invoices)
			setCursorUrls([...cursorUrls, nextCursorUrl])
		}
		setInvoicesLoading(false)
	}, [state.paddleCustomerId])

	const prevPageAvailable = page > 1
	const nextPageAvailable = cursorUrls[page] !== null

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				padding: "1rem 1rem 1rem 1rem",
			}}
		>
			<h1
				style={{
					fontSize: "1.3rem",
					lineHeight: "1.1rem",
					fontWeight: "600",
				}}
			>
				{t("account.invoice_history")}
			</h1>
			<span
				style={{
					fontSize: "0.8rem",
					fontWeight: "500",
					color: "#777",
					marginBottom: "1rem",
				}}
			>
				{t("account.subscription_for")}{" "}
				<span style={{ textDecoration: "underline" }}>
					{state.shop}
				</span>
			</span>

			<div style={{ display: "flex", flexDirection: "column" }}>
				{invoicesLoading ? (
					<div
						style={{
							height: "10rem",
							width: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							border: "1px dashed #777",
							background: "#efefef",
							borderRadius: "0.5rem",
							gap: "0.5rem",
						}}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="1rem"
							height="1rem"
							viewBox="0 0 24 24"
						>
							<g>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".14"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".29"
									transform="rotate(30 12 12)"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".43"
									transform="rotate(60 12 12)"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".57"
									transform="rotate(90 12 12)"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".71"
									transform="rotate(120 12 12)"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									opacity=".86"
									transform="rotate(150 12 12)"
								></rect>
								<rect
									width="2"
									height="5"
									x="11"
									y="1"
									fill="currentColor"
									transform="rotate(180 12 12)"
								></rect>
								<animateTransform
									attributeName="transform"
									calcMode="discrete"
									dur="0.75s"
									repeatCount="indefinite"
									type="rotate"
									values="0 12 12;30 12 12;60 12 12;90 12 12;120 12 12;150 12 12;180 12 12;210 12 12;240 12 12;270 12 12;300 12 12;330 12 12;360 12 12"
								></animateTransform>
							</g>
						</svg>
						<span style={{ fontSize: "0.8rem" }}>
							{t("account.loading_invoices")}
						</span>
					</div>
				) : invoices.length ? (
					invoices.map((invoice) => {
						const date = new Date(invoice.billed_at).toDateString()
						const price = parseInt(invoice.amount) / 100
						const currencyCode = invoice.currency_code || "USD"
						const priceDisplayed = new Intl.NumberFormat("en-US", {
							style: "currency",
							currency: currencyCode,
						}).format(price)

						return (
							<div
								key={invoice.id}
								style={{
									display: "flex",
									justifyContent: "flex-end",
									alignItems: "start",
									gap: "0.5rem",
								}}
							>
								<div
									style={{
										marginRight: "auto",
										display: "flex",
										alignItems: "start",
										gap: "0.3rem",
									}}
								>
									<svg
										style={{ color: "#333" }}
										width="1.2rem"
										height="1.2rem"
										viewBox="0 0 24 24"
									>
										<g
											fill="none"
											stroke="currentColor"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
										>
											<path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
											<path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2zM9 7h1m-1 6h6m-2 4h2"></path>
										</g>
									</svg>
									<div>
										<span
											style={{
												fontSize: "0.9rem",
												fontWeight: "500",
												verticalAlign: "top",
												color: "#333",
											}}
										>
											{date}
										</span>
										{invoice?.items?.length
											? invoice.items.map((item) => {
													return (
														<div
															key={item.id}
															style={{
																marginBottom:
																	"0.5rem",
																fontSize:
																	"0.7rem",
																fontWeight:
																	"400",
																color: "#777",
															}}
														>
															• {item.name}
														</div>
													)
												})
											: null}
									</div>
								</div>
								<div
									style={{
										padding: "0.1rem 0.4rem",
										borderRadius: "0.5rem",
										background: "#eee",
										color: "#333",
										fontSize: "0.8rem",
										fontWeight: "600",
									}}
								>
									{priceDisplayed}
								</div>
								<a
									href={invoice.invoiceUrl}
									target="_blank"
									rel="noreferrer"
									style={{
										fontSize: "0.8rem",
										color: "#333",
									}}
								>
									{t("common.download")}
								</a>
							</div>
						)
					})
				) : (
					<div
						style={{
							height: "5rem",
							width: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							border: "1px dashed #777",
							background: "#efefef",
							borderRadius: "0.5rem",
						}}
					>
						<span style={{ fontSize: "0.8rem" }}>
							{t("account.no_invoices_found")}
						</span>
					</div>
				)}
			</div>

			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					gap: "0.5rem",
				}}
			>
				{prevPageAvailable && (
					<svg
						style={{ cursor: "pointer" }}
						onClick={() => changePage(page - 1)}
						width="1rem"
						height="1rem"
						viewBox="0 0 24 24"
					>
						<path
							fill="currentColor"
							d="m11.475 18.025l-8.15-5.175q-.475-.3-.475-.85t.475-.85l8.15-5.175q.5-.325 1.012-.037t.513.887V11h8q.425 0 .713.288T22 12q0 .425-.288.713T21 13h-8v4.175q0 .6-.513.888t-1.012-.038Z"
						></path>
					</svg>
				)}
				{(prevPageAvailable || nextPageAvailable) && (
					<span style={{ fontSize: "1rem", fontWeight: "500" }}>
						{page}
					</span>
				)}
				{nextPageAvailable && (
					<svg
						style={{ cursor: "pointer" }}
						onClick={() => changePage(page + 1)}
						width="1rem"
						height="1rem"
						viewBox="0 0 24 24"
					>
						<path
							fill="currentColor"
							d="M12.525 18.025q-.5.325-1.012.038T11 17.175V13H3q-.425 0-.713-.288T2 12q0-.425.288-.713T3 11h8V6.825q0-.6.513-.888t1.012.038l8.15 5.175q.475.3.475.85t-.475.85l-8.15 5.175Z"
						></path>
					</svg>
				)}
			</div>
		</div>
	)
}
